import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useT, taskTitle } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import { Card, Loader, PageTitle } from "@/components/kv";

const POSITIONS = ["asci", "asci_yardimcisi", "sanat_hocasi", "barista_head", "barista", "temizlik"];

export const TaskDialog = ({ task, position, staff = [], presetUser, onClose, onSaved }) => {
  const { t } = useT();
  const [f, setF] = useState(task
    ? { position: task.position, period: task.period, title_tr: task.title.tr, title_en: task.title.en || "", title_ru: task.title.ru || "", user_id: task.user_id || "", date: task.date || "" }
    : { position: presetUser?.position || position, period: "morning", title_tr: "", title_en: "", title_ru: "", user_id: presetUser?.id || "", date: "" });
  const [once, setOnce] = useState(!!task?.date);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setUser = (e) => {
    const u = staff.find((s) => s.id === e.target.value);
    setF((s) => ({ ...s, user_id: e.target.value, position: u ? u.position : s.position }));
  };
  const save = async () => {
    const body = { ...f, user_id: f.user_id || null, date: once && f.date ? f.date : null };
    try {
      if (task) await api.put(`/admin/tasks/${task.id}`, body);
      else await api.post("/admin/tasks", body);
      toast.success(t("common.saved"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, t));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FAF6F0] rounded-2xl" data-testid="task-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{task ? t("tadmin.edit") : t("tadmin.add")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>{t("tadmin.assignee")}</Label>
            <select className="kv-input" value={f.user_id} onChange={setUser} data-testid="task-assignee-select">
              <option value="">{t("tadmin.everyone")}</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name} — {t(`positions.${s.position}`)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>{t("common.position")}</Label>
              <select className="kv-input" value={f.position} onChange={set("position")} disabled={!!f.user_id} data-testid="task-position-select">
                {POSITIONS.map((p) => <option key={p} value={p}>{t(`positions.${p}`)}</option>)}
              </select>
            </div>
            <div className="space-y-1"><Label>{t("tadmin.period")}</Label>
              <select className="kv-input" value={f.period} onChange={set("period")} data-testid="task-period-select">
                {["morning", "evening"].map((p) => <option key={p} value={p}>{t(`periods.${p}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>{t("tadmin.repeat")}</Label>
              <select className="kv-input" value={once ? "once" : "daily"} onChange={(e) => setOnce(e.target.value === "once")} data-testid="task-repeat-select">
                <option value="daily">{t("tadmin.daily")}</option>
                <option value="once">{t("tadmin.once")}</option>
              </select>
            </div>
            {once && <div className="space-y-1"><Label>{t("common.date")}</Label><Input type="date" value={f.date} onChange={set("date")} className="bg-white" data-testid="task-date-input" /></div>}
          </div>
          <div className="space-y-1"><Label>{t("tadmin.title_tr")}</Label><Input value={f.title_tr} onChange={set("title_tr")} className="bg-white" data-testid="task-title-tr-input" /></div>
          <div className="space-y-1"><Label>{t("tadmin.title_en")} <span className="text-stone-400">({t("common.optional")})</span></Label><Input value={f.title_en} onChange={set("title_en")} className="bg-white" data-testid="task-title-en-input" /></div>
          <div className="space-y-1"><Label>{t("tadmin.title_ru")} <span className="text-stone-400">({t("common.optional")})</span></Label><Input value={f.title_ru} onChange={set("title_ru")} className="bg-white" data-testid="task-title-ru-input" /></div>
        </div>
        <DialogFooter><Button className="rounded-full" onClick={save} disabled={f.title_tr.trim().length < 2 || (once && !f.date)} data-testid="task-save-button">{t("common.save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function TasksAdmin() {
  const { t, lang } = useT();
  const [tasks, setTasks] = useState(null);
  const [position, setPosition] = useState("asci");
  const [dialog, setDialog] = useState(null);
  const [staff, setStaff] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/tasks").then((r) => setTasks(r.data)).catch((e) => toast.error(errMsg(e, t)));
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    await api.delete(`/admin/tasks/${id}`);
    toast.success(t("common.deleted"));
    load();
  };

  return (
    <div>
      <PageTitle kicker={t("adminnav.tasks")} title={t("tadmin.title")}>
        <Button className="rounded-full" onClick={() => setDialog({})} data-testid="task-add-button"><Plus className="h-4 w-4 mr-1" />{t("tadmin.add")}</Button>
      </PageTitle>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4" data-testid="task-position-filter">
        {POSITIONS.map((p) => (
          <button key={p} onClick={() => setPosition(p)} data-testid={`task-filter-${p}`}
            className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm transition-colors ${position === p ? "bg-[#b8563a] text-white border-[#b8563a]" : "bg-white border-[#E8DFD0] text-stone-600"}`}>
            {t(`positions.${p}`)}
          </button>
        ))}
      </div>
      {!tasks ? <Loader /> : (
        <div className="grid md:grid-cols-2 gap-5">
          {["morning", "evening"].map((period) => (
            <Card key={period} className="p-5" data-testid={`task-admin-${period}`}>
              <h2 className="font-display text-2xl font-bold mb-3">{t(`periods.${period}`)}</h2>
              <ul className="space-y-2">
                {tasks.filter((x) => x.position === position && x.period === period).map((task) => (
                  <li key={task.id} className="flex items-center gap-2 rounded-xl bg-[#FAF6F0] px-3 py-2.5">
                    <span className="flex-1 text-sm">
                      {taskTitle(task, lang)}
                      {(task.user_id || task.date) && (
                        <span className="flex flex-wrap gap-1 mt-1" data-testid={`task-meta-${task.id}`}>
                          {task.user_id && <span className="rounded-full bg-[#fbe9dc] text-[#99432b] text-[11px] font-semibold px-2 py-0.5">{staff.find((s) => s.id === task.user_id)?.name || t("tadmin.personal")}</span>}
                          {task.date && <span className="rounded-full bg-white border border-[#E8DFD0] text-stone-600 text-[11px] font-mono-kv px-2 py-0.5">{task.date}</span>}
                        </span>
                      )}
                    </span>
                    <Button variant="ghost" size="icon" onClick={() => setDialog({ task })} data-testid={`task-edit-${task.id}`} aria-label={t("common.edit")}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(task.id)} data-testid={`task-delete-${task.id}`} aria-label={t("common.delete")}><Trash2 className="h-4 w-4 text-red-700" /></Button>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
      {dialog && <TaskDialog task={dialog.task} position={position} staff={staff} onClose={() => setDialog(null)} onSaved={load} />}
    </div>
  );
}
